<div class="container-fluid complyon-page finance-page px-3 px-md-4 px-xl-4 py-3">
  <div class="complyon-shell finance-shell mx-auto">
    <section class="mb-3">
      <h1 class="complyon-title finance-title mb-2 text-l">Finance compliance</h1>
      <p class="complyon-subtitle finance-subtitle mb-0 text-s">
        Budget controls, invoice matching, payment approval, tax calendar and segregation of duties.
      </p>
    </section>

    <section class="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-3">
      <div class="d-flex flex-wrap align-items-center gap-1">
        <button
          v-for="tab in tabs"
          :key="tab.id"
          type="button"
          class="btn doa-tab-btn fw-semibold"
          :class="{ active: active_tab === tab.id }"
          @click="set_tab(tab.id)"
        >
          <span>{{ tab.label }}</span>
          <span v-if="tab.count !== undefined" class="doa-tab-count">{{ tab.count }}</span>
        </button>
      </div>
      <button type="button" class="btn doa-reset-btn d-inline-flex align-items-center gap-1">
        <i class="bi bi-arrow-clockwise"></i>
        <span>Reset sample</span>
      </button>
    </section>

    <div v-if="active_tab === 'payment_gate'">
      <section class="mb-3">
        <div class="rounded-4 border px-3 py-3" style="background:#e8f8f5;border-color:#bce9df;">
          <div class="d-flex align-items-start gap-2 text-s">
            <i class="bi bi-shield-check mt-1 text-success"></i>
            <div>
              <strong>Payment Compliance Gate.</strong>
              No supplier is cleared for payment unless every required compliance record is valid
              supplier status, PO, goods received, 3-way match, approval threshold and no duplicate.
            </div>
          </div>
        </div>
      </section>

      <section class="row g-3 mb-3">
        <div v-for="card in payment_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card finance-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label finance-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value finance-stat-value text-l" :class="card.accent === 'red' ? 'text-danger' : 'text-success'">
              {{ card.value }}
            </div>
            <div class="complyon-stat-note finance-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel finance-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title finance-section-title text-s">Payment queue</div>
            <div class="complyon-section-subtitle finance-section-subtitle text-xs">Every invoice and its gate verdict. Open one to inspect the compliance gate.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_invoice_modal">
            <i class="bi bi-plus-lg"></i>
            <span>New invoice</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table finance-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">Invoice</th>
                <th>Supplier</th>
                <th>Amount</th>
                <th>Gate</th>
                <th>Verdict</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in payment_rows"
                :key="row.invoice"
                :class="{ 'bg-light': row.highlighted, 'table-row finance-table-row': row.drawer_key }"
                :role="row.drawer_key ? 'button' : null"
                :tabindex="row.drawer_key ? 0 : null"
                @click="open_drawer(row.drawer_key)"
                @keydown.enter="open_drawer(row.drawer_key)"
                @keydown.space.prevent="open_drawer(row.drawer_key)"
              >
                <td class="ps-3"><div class="finance-invoice">{{ row.invoice }}</div></td>
                <td><div class="finance-supplier">{{ row.supplier }}</div></td>
                <td class="fw-semibold">{{ row.amount }}</td>
                <td>
                  <div class="d-flex align-items-center gap-1">
                    <span
                      v-for="(segment, index) in row.gate"
                      :key="`${row.invoice}-${index}`"
                      class="d-inline-block rounded-1"
                      :style="{ width: '10px', height: '10px', background: gate_dot_color(segment) }"
                    ></span>
                  </div>
                </td>
                <td><span class="badge complyon-badge finance-badge text-xs" :class="row.verdict.class">{{ row.verdict.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'budget'">
      <section class="row g-3 mb-3">
        <div v-for="card in budget_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card finance-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label finance-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value finance-stat-value text-l" :class="card.accent === 'red' ? 'text-danger' : 'text-dark'">{{ card.value }}</div>
            <div class="complyon-stat-note finance-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel finance-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title finance-section-title text-s">Budget compliance</div>
            <div class="complyon-section-subtitle finance-section-subtitle text-xs">Committed spend vs budget per line. Over-budget lines block further payments at the gate.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_budget_modal">
            <i class="bi bi-plus-lg"></i>
            <span>Add budget line</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table finance-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">Budget line</th>
                <th>Budget</th>
                <th>Committed</th>
                <th>Utilisation</th>
                <th>Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in budget_rows"
                :key="row.id"
                :class="{ 'bg-light': row.highlighted, 'finance-table-row': true }"
                role="button"
                tabindex="0"
                @click="open_edit_budget_modal(row)"
                @keydown.enter.prevent="open_edit_budget_modal(row)"
                @keydown.space.prevent="open_edit_budget_modal(row)"
              >
                <td class="ps-3"><div class="finance-supplier">{{ row.line }}</div></td>
                <td>{{ row.budget }}</td>
                <td :class="row.committed_class || ''">{{ row.committed }}</td>
                <td>
                  <div class="d-flex align-items-center gap-2">
                    <div class="finance-mini-bars" style="width:120px;min-width:120px;">
                      <span
                        v-for="n in 10"
                        :key="`${row.line}-${n}`"
                        :style="{
                          opacity: n <= Math.max(1, Math.round(row.utilisation / 10)) ? 1 : 0.15,
                          height: '6px',
                          background: row.utilisation >= 100 ? '#be2b20' : row.utilisation >= 80 ? '#c4580b' : '#10855e'
                        }"
                      ></span>
                    </div>
                    <strong>{{ row.utilisation_label }}</strong>
                  </div>
                </td>
                <td><span class="badge complyon-badge finance-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'overrides'">
      <section class="complyon-panel finance-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title finance-section-title text-s">Manual override register</div>
            <div class="complyon-section-subtitle finance-section-subtitle text-xs">Every manual release of the payment gate - who, why and when. The first place auditors look.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_override_modal">
            <i class="bi bi-plus-lg"></i>
            <span>Log override</span>
          </button>
        </div>

        <div class="table-responsive">
          <table class="table finance-table align-middle mb-0">
            <thead>
              <tr>
                <th class="ps-3">Invoice</th>
                <th>Reason</th>
                <th>Authorised by</th>
                <th>Date</th>
                <th>Status</th>
                <th class="pe-3"></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="row in override_rows"
                :key="row.id"
                class="finance-table-row"
                role="button"
                tabindex="0"
                @click="open_edit_override_modal(row)"
                @keydown.enter.prevent="open_edit_override_modal(row)"
                @keydown.space.prevent="open_edit_override_modal(row)"
              >
                <td class="ps-3"><div class="finance-invoice">{{ row.invoice }}</div></td>
                <td>{{ row.reason }}</td>
                <td>{{ row.authorised_by }}</td>
                <td class="fw-semibold">{{ row.date }}</td>
                <td><span class="badge complyon-badge finance-badge text-xs" :class="row.status.class">{{ row.status.label }}</span></td>
                <td class="pe-3 text-end"><i class="bi bi-chevron-right finance-arrow"></i></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
    </div>

    <div v-else-if="active_tab === 'irregular'">
      <section class="row g-3 mb-3">
        <div v-for="card in irregular_cards" :key="card.label" class="col-12 col-md-6 col-xl-3">
          <article class="complyon-card finance-card rounded-4 p-3 h-100">
            <div class="complyon-stat-label finance-stat-label text-xs">{{ card.label }}</div>
            <div class="complyon-stat-value finance-stat-value text-l" :class="card.accent === 'amber' ? 'text-warning-emphasis' : 'text-danger'">{{ card.value }}</div>
            <div class="complyon-stat-note finance-stat-note mt-3 text-xs">{{ card.note }}</div>
          </article>
        </div>
      </section>

      <section class="complyon-panel finance-panel rounded-4 overflow-hidden">
        <div class="d-flex align-items-start justify-content-between gap-2 px-3 pt-3">
          <div>
            <div class="complyon-section-title finance-section-title text-s">Irregular & fruitless/wasteful expenditure</div>
            <div class="complyon-section-subtitle finance-section-subtitle text-xs">Indicators flagged for the audit committee - payments that breached process or wasted funds.</div>
          </div>
          <button type="button" class="btn config-navbar-primary-action finance-action-btn" @click="open_irregular_modal">
            <i class="bi bi-plus-lg"></i>
            <span>Log indicator</span>
          </button>
        </div>

        <div class="px-3 pb-2">
          <div
            v-for="row in irregular_rows"
            :key="row.id"
            class="procurement-request finance-table-row d-flex align-items-center justify-content-between gap-3"
            :class="{ 'bg-light px-2 rounded-3': row.highlighted }"
            role="button"
            tabindex="0"
            @click="open_edit_irregular_modal(row)"
            @keydown.enter.prevent="open_edit_irregular_modal(row)"
            @keydown.space.prevent="open_edit_irregular_modal(row)"
          >
            <div class="d-flex align-items-start gap-2 min-w-0">
              <span
                class="procurement-request-icon d-inline-flex align-items-center justify-content-center"
                :style="row.icon_style"
              >
                <i class="bi" :class="row.icon"></i>
              </span>
              <div class="min-w-0">
                <div class="procurement-request-title text-truncate">{{ row.title }}</div>
                <div class="procurement-request-meta">{{ row.meta }}</div>
              </div>
            </div>

            <div class="d-flex flex-column align-items-end gap-1 flex-shrink-0">
              <span class="badge rounded-pill finance-badge" :class="row.badge_class">{{ row.badge }}</span>
              <div class="procurement-progress-value">{{ row.amount }}</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </div>

  <div v-if="active_drawer" class="command-centre-drawer-backdrop" @click="close_drawer">
    <aside v-if="active_drawer" class="command-centre-drawer finance-payment-drawer" role="dialog" aria-modal="true" :aria-label="active_drawer.supplier" @click.stop>
      <div class="command-centre-drawer-header border-bottom finance-payment-drawer-header">
        <div class="d-flex align-items-start justify-content-between gap-3">
          <div class="min-w-0">
            <div class="command-centre-drawer-kicker text-uppercase">{{ active_drawer.section }}</div>
            <h2 class="command-centre-drawer-title mb-1 text-truncate finance-payment-drawer-title">{{ active_drawer.supplier }}</h2>
            <div class="command-centre-drawer-subtitle text-truncate">{{ active_drawer.amount }} - {{ active_drawer.po }}</div>
          </div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <span class="badge rounded-pill" :class="active_drawer.status_class">{{ active_drawer.status }}</span>
            <button type="button" class="btn btn-link text-decoration-none p-0 command-centre-drawer-close" @click="close_drawer" aria-label="Close drawer">
              <i class="bi bi-x-lg fs-5"></i>
            </button>
          </div>
        </div>
      </div>

      <div ref="drawerBody" class="command-centre-drawer-body finance-payment-drawer-body">
        <section class="finance-gate-banner rounded-4" :class="`is-${active_drawer.gate_tone}`">
          <div class="d-flex align-items-start justify-content-between gap-3">
            <div class="d-flex align-items-start gap-3">
              <span class="finance-gate-banner-icon">
                <i class="bi" :class="active_drawer.gate_tone === 'success' ? 'bi-check-circle' : 'bi-exclamation-octagon'"></i>
              </span>
              <div>
                <div class="finance-gate-banner-title">{{ active_drawer.gate_title }}</div>
                <div class="finance-gate-banner-copy">{{ active_drawer.gate_summary }}</div>
              </div>
            </div>
            <div class="finance-gate-banner-score">{{ active_drawer.score }}</div>
          </div>
        </section>

        <section class="mt-4">
          <div class="d-flex align-items-center justify-content-between gap-2 mb-3">
            <div class="finance-drawer-section-label">Gate checks</div>
            <div class="finance-drawer-section-helper">Click to toggle</div>
          </div>

          <div class="d-flex flex-column gap-2">
            <article v-for="check in active_drawer.checks" :key="check.label" class="finance-gate-check">
              <div class="d-flex align-items-start gap-3">
                <span class="finance-gate-check-icon" :class="check.result === 'pass' ? 'is-pass' : 'is-fail'">
                  <i class="bi" :class="check.result === 'pass' ? 'bi-check-circle' : 'bi-x-circle'"></i>
                </span>
                <div class="min-w-0 flex-grow-1">
                  <div class="d-flex align-items-center gap-2 flex-wrap">
                    <div class="finance-gate-check-title">{{ check.label }}</div>
                    <span class="finance-gate-check-tag">Blocking</span>
                  </div>
                  <div class="finance-gate-check-copy">{{ check.detail }}</div>
                </div>
                <span class="finance-gate-check-status" :class="check.result === 'pass' ? 'is-pass' : 'is-fail'">
                  {{ check.result_label }}
                </span>
              </div>
            </article>
          </div>
        </section>

        <section v-if="active_drawer.quick_link" class="mt-4">
          <button type="button" class="btn btn-link finance-drawer-link text-decoration-none p-0">
            <i class="bi bi-box-arrow-up-right"></i>
            <span>{{ active_drawer.quick_link }}</span>
          </button>
        </section>

        <section class="mt-4">
          <div class="finance-drawer-section-label mb-2">Compliance note</div>
          <div class="finance-drawer-note">{{ active_drawer.note }}</div>
        </section>

        <section class="mt-3">
          <button type="button" class="btn finance-drawer-secondary-btn">
            <i class="bi bi-archive"></i>
            <span>Export audit evidence</span>
          </button>
        </section>
      </div>

      <footer class="command-centre-drawer-footer border-top finance-payment-drawer-footer">
        <div class="d-flex align-items-center justify-content-between gap-3 w-100">
          <div class="finance-drawer-footer-hint">
            <i class="bi bi-exclamation-triangle"></i>
            <span>{{ active_drawer.footer_hint || 'Ready for release' }}</span>
          </div>
          <button type="button" class="btn finance-drawer-footer-btn" :class="active_drawer.footer_action_class">
            <i class="bi" :class="active_drawer.footer_action_icon"></i>
            <span>{{ active_drawer.footer_action }}</span>
          </button>
        </div>
      </footer>
    </aside>
  </div>

  <div v-if="invoice_modal_open" class="finance-modal-backdrop" @click.self="close_invoice_modal">
    <section class="finance-modal-shell" role="dialog" aria-modal="true" aria-label="New invoice">
      <header class="finance-modal-header d-flex align-items-start justify-content-between gap-3 border-bottom">
        <div>
          <div class="finance-modal-title">New invoice</div>
          <div class="finance-modal-subtitle">An invoice in the payment queue. Gate checks are set in the payment detail.</div>
        </div>
        <button type="button" class="btn btn-link p-0 finance-modal-close" @click="close_invoice_modal" aria-label="Close modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="finance-modal-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="finance-modal-label">Supplier</label>
            <input v-model="invoice_form.supplier" type="text" class="form-control finance-modal-input" placeholder="Supplier name">
          </div>

          <div class="col-12 col-md-6">
            <label class="finance-modal-label">Amount (R)</label>
            <input v-model="invoice_form.amount" type="text" class="form-control finance-modal-input" placeholder="Amount">
          </div>

          <div class="col-12 col-md-6">
            <label class="finance-modal-label">Purchase order</label>
            <input v-model="invoice_form.purchase_order" type="text" class="form-control finance-modal-input" placeholder="e.g. PO-1184 or -">
          </div>

          <div class="col-12">
            <label class="finance-modal-label">Budget line</label>
            <input v-model="invoice_form.budget_line" type="text" class="form-control finance-modal-input" placeholder="e.g. ICT - hardware">
          </div>

          <div class="col-12">
            <label class="finance-modal-label">Note</label>
            <textarea v-model="invoice_form.note" rows="3" class="form-control finance-modal-textarea" placeholder="Compliance context"></textarea>
          </div>
        </div>
      </div>

      <footer class="finance-modal-footer d-flex align-items-center justify-content-end gap-2 border-top">
        <button type="button" class="btn btn-link finance-modal-cancel text-decoration-none" @click="close_invoice_modal">Cancel</button>
        <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="submit_invoice_modal">
          <i class="bi bi-plus-lg"></i>
          <span>Create</span>
        </button>
      </footer>
    </section>
  </div>

  <div v-if="budget_modal_open" class="finance-modal-backdrop" @click.self="close_budget_modal">
    <section class="finance-modal-shell" role="dialog" aria-modal="true" :aria-label="budget_modal_mode === 'edit' ? 'Edit budget line' : 'New budget line'">
      <header class="finance-modal-header d-flex align-items-start justify-content-between gap-3 border-bottom">
        <div>
          <div class="finance-modal-title">{{ budget_modal_mode === 'edit' ? 'Edit budget line' : 'New budget line' }}</div>
          <div class="finance-modal-subtitle">A budget line; payments are checked for available funds against it.</div>
        </div>
        <button type="button" class="btn btn-link p-0 finance-modal-close" @click="close_budget_modal" aria-label="Close modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="finance-modal-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="finance-modal-label">Budget line</label>
            <input v-model="budget_form.line" type="text" class="form-control finance-modal-input" placeholder="e.g. ICT - hardware">
          </div>

          <div class="col-12 col-md-6">
            <label class="finance-modal-label">Budget (R)</label>
            <input v-model="budget_form.budget" type="text" class="form-control finance-modal-input" placeholder="Amount">
          </div>

          <div class="col-12 col-md-6">
            <label class="finance-modal-label">Committed (R)</label>
            <input v-model="budget_form.committed" type="text" class="form-control finance-modal-input" placeholder="Amount">
          </div>

          <div class="col-12">
            <label class="finance-modal-label">Status</label>
            <select v-model="budget_form.status" class="form-select finance-modal-input">
              <option v-for="option in budget_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>
      </div>

      <footer class="finance-modal-footer d-flex align-items-center justify-content-between gap-2 border-top">
        <button v-if="budget_modal_mode === 'edit'" type="button" class="btn btn-link finance-modal-delete text-decoration-none" @click="delete_budget_modal">
          <i class="bi bi-trash"></i>
          <span>Delete</span>
        </button>
        <span v-else></span>
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-link finance-modal-cancel text-decoration-none" @click="close_budget_modal">Cancel</button>
          <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="submit_budget_modal">
            <i class="bi" :class="budget_modal_mode === 'edit' ? 'bi-check2-square' : 'bi-plus-lg'"></i>
            <span>{{ budget_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
          </button>
        </div>
      </footer>
    </section>
  </div>

  <div v-if="override_modal_open" class="finance-modal-backdrop" @click.self="close_override_modal">
    <section class="finance-modal-shell" role="dialog" aria-modal="true" :aria-label="override_modal_mode === 'edit' ? 'Edit manual override' : 'New manual override'">
      <header class="finance-modal-header d-flex align-items-start justify-content-between gap-3 border-bottom">
        <div>
          <div class="finance-modal-title">{{ override_modal_mode === 'edit' ? 'Edit manual override' : 'New manual override' }}</div>
          <div class="finance-modal-subtitle">A logged manual override of the payment gate - auditable.</div>
        </div>
        <button type="button" class="btn btn-link p-0 finance-modal-close" @click="close_override_modal" aria-label="Close modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="finance-modal-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="finance-modal-label">Invoice</label>
            <input v-model="override_form.invoice" type="text" class="form-control finance-modal-input" placeholder="e.g. INV-2237">
          </div>

          <div class="col-12">
            <label class="finance-modal-label">Reason</label>
            <input v-model="override_form.reason" type="text" class="form-control finance-modal-input" placeholder="Why the gate was overridden">
          </div>

          <div class="col-12 col-md-6">
            <label class="finance-modal-label">Authorised by</label>
            <input v-model="override_form.authorised_by" type="text" class="form-control finance-modal-input" placeholder="Name (role)">
          </div>

          <div class="col-12 col-md-6">
            <label class="finance-modal-label">Date</label>
            <input v-model="override_form.date" type="text" class="form-control finance-modal-input" placeholder="e.g. 12 May 2026">
          </div>

          <div class="col-12">
            <label class="finance-modal-label">Status</label>
            <select v-model="override_form.status" class="form-select finance-modal-input">
              <option v-for="option in override_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>
      </div>

      <footer class="finance-modal-footer d-flex align-items-center justify-content-between gap-2 border-top">
        <button v-if="override_modal_mode === 'edit'" type="button" class="btn btn-link finance-modal-delete text-decoration-none" @click="delete_override_modal">
          <i class="bi bi-trash"></i>
          <span>Delete</span>
        </button>
        <span v-else></span>
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-link finance-modal-cancel text-decoration-none" @click="close_override_modal">Cancel</button>
          <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="submit_override_modal">
            <i class="bi" :class="override_modal_mode === 'edit' ? 'bi-check2-square' : 'bi-plus-lg'"></i>
            <span>{{ override_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
          </button>
        </div>
      </footer>
    </section>
  </div>

  <div v-if="irregular_modal_open" class="finance-modal-backdrop" @click.self="close_irregular_modal">
    <section class="finance-modal-shell" role="dialog" aria-modal="true" :aria-label="irregular_modal_mode === 'edit' ? 'Edit expenditure flag' : 'New expenditure flag'">
      <header class="finance-modal-header d-flex align-items-start justify-content-between gap-3 border-bottom">
        <div>
          <div class="finance-modal-title">{{ irregular_modal_mode === 'edit' ? 'Edit expenditure flag' : 'New expenditure flag' }}</div>
          <div class="finance-modal-subtitle">An irregular or fruitless & wasteful expenditure indicator.</div>
        </div>
        <button type="button" class="btn btn-link p-0 finance-modal-close" @click="close_irregular_modal" aria-label="Close modal">
          <i class="bi bi-x-lg"></i>
        </button>
      </header>

      <div class="finance-modal-body">
        <div class="row g-3">
          <div class="col-12">
            <label class="finance-modal-label">Flag</label>
            <input v-model="irregular_form.title" type="text" class="form-control finance-modal-input" placeholder="e.g. Payment without PO">
          </div>

          <div class="col-12 col-md-6">
            <label class="finance-modal-label">Type</label>
            <select v-model="irregular_form.type" class="form-select finance-modal-input">
              <option v-for="option in irregular_type_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>

          <div class="col-12 col-md-6">
            <label class="finance-modal-label">Invoice</label>
            <input v-model="irregular_form.invoice" type="text" class="form-control finance-modal-input" placeholder="e.g. INV-2199">
          </div>

          <div class="col-12 col-md-6">
            <label class="finance-modal-label">Amount (R)</label>
            <input v-model="irregular_form.amount" type="text" class="form-control finance-modal-input" placeholder="Amount">
          </div>

          <div class="col-12">
            <label class="finance-modal-label">Detail</label>
            <textarea v-model="irregular_form.detail" rows="3" class="form-control finance-modal-textarea" placeholder="What happened"></textarea>
          </div>

          <div class="col-12">
            <label class="finance-modal-label">Status</label>
            <select v-model="irregular_form.status" class="form-select finance-modal-input">
              <option v-for="option in irregular_status_options" :key="option" :value="option">{{ option }}</option>
            </select>
          </div>
        </div>
      </div>

      <footer class="finance-modal-footer d-flex align-items-center justify-content-between gap-2 border-top">
        <button v-if="irregular_modal_mode === 'edit'" type="button" class="btn btn-link finance-modal-delete text-decoration-none" @click="delete_irregular_modal">
          <i class="bi bi-trash"></i>
          <span>Delete</span>
        </button>
        <span v-else></span>
        <div class="d-flex align-items-center gap-2">
          <button type="button" class="btn btn-link finance-modal-cancel text-decoration-none" @click="close_irregular_modal">Cancel</button>
          <button type="button" class="btn config-navbar-primary-action d-inline-flex align-items-center gap-2" @click="submit_irregular_modal">
            <i class="bi" :class="irregular_modal_mode === 'edit' ? 'bi-check2-square' : 'bi-plus-lg'"></i>
            <span>{{ irregular_modal_mode === 'edit' ? 'Save changes' : 'Create' }}</span>
          </button>
        </div>
      </footer>
    </section>
  </div>
</div>
